<!-- A menu to pick content types (any of 'radio', 'tv', 'tickers').

How to use:
	- Add this component.
	- Listen to the @selected event to get a list of content types.

	You can get an initial value via a ref, e.g. with ref="menu" use

		let ctypes = this.$refs.menu.ctypes;

-->

<template>
  <v-menu
    v-model="showMenu"
    offset-y
    bottom
    right
    class="pt-3"
    content-class="max-width-90"
    :close-on-content-click="false"
  >
    <v-btn
      slot="activator"
      icon
      class="ma-0"
    >
      <v-icon
        :class="{'grey--text text--darken-1': !dirty, 'msr--text': dirty}"
      >
        filter_list
      </v-icon>
    </v-btn>
    <v-list class="pt-0 typefilter-menu no-scrollbar">
      <!-- Clear Filters Button -->
      <v-list-tile class="no-padding">
        <v-btn
          block
          flat
          class="pl-0 grey--text"
          @click="reset()"
        >
          <v-layout align-center>
            <v-icon class="ml-3 mr-2">
              clear
            </v-icon>
            {{ $t('clearFilters') }}
          </v-layout>
        </v-btn>
      </v-list-tile>
      <v-divider />
      <!-- Radio/TV/Ticker Filters -->
      <v-list-tile class="list-tile-auto-height">
        <v-layout column>
          <v-checkbox
            v-if="!noRadio"
            :disabled="disabled"
            class="msr--text mt-1"
            color="msr"
            hide-details
            :label="'Radio ' + $t('spots')"
            :input-value="contentsQueryRadio"
            @change="() => (contentsQueryRadio = !contentsQueryRadio)"
          />
          <v-checkbox
            v-if="!noTv"
            :disabled="disabled"
            class="msr--text mt-1"
            color="msr"
            hide-details
            :label="'TV ' + $t('clips')"
            :input-value="contentsQueryTV"
            @change="() => (contentsQueryTV = !contentsQueryTV)"
          />
          <v-checkbox
            v-if="!noTickers"
            :disabled="disabled"
            class="msr--text mt-1 mb-2"
            color="msr"
            hide-details
            :label="'TV ' + $t('tickers')"
            :input-value="contentsQueryTickers"
            @change="() => (contentsQueryTickers = !contentsQueryTickers)"
          />
        </v-layout>
      </v-list-tile>
      <v-divider />
      <v-list-tile
        :disabled="disabled"
        class="list-tile-auto-height"
      >
        <v-layout
          column
          class="mt-1 mb-2"
        >
          <!-- Editable only Filter -->
          <v-layout align-center>
            <v-checkbox
              class="mr-2 mt-0"
              color="msr"
              hide-details
              :label="$t('editableOnly')"
              :title="$t('onlyEditableSpots')"
              :input-value="contentsQueryEditableOnly"
              @change="
                () => (contentsQueryEditableOnly = !contentsQueryEditableOnly)
              "
            />
            <v-icon
              :color="contentsQueryEditableOnly ? 'msr' : 'grey'"
              class="mt-1"
            >
              edit
            </v-icon>
          </v-layout>
          <!-- On-Air only Filter -->
          <v-layout
            align-center
            data-cy="on-air-only-filter"
          >
            <v-checkbox
              class="mr-2 mt-1"
              color="msr"
              hide-details
              :label="$t('onAirOnly')"
              :title="$t('onlyActiveSpots')"
              :input-value="contentsQueryOnAirOnly"
              @change="() => (contentsQueryOnAirOnly = !contentsQueryOnAirOnly)"
            />
            <on-air-indicator
              :show-tooltip="false"
              :is-on-air="true"
              :is-planned-to-air="true"
              class="mt-1"
              style="transform: scale(1.2)"
            />
          </v-layout>
        </v-layout>
      </v-list-tile>
      <v-divider v-if="canSeePublishFilter" />
      <!-- (Un)published only Filter -->
      <v-list-tile
        v-if="canSeePublishFilter"
        class="list-tile-auto-height"
      >
        <v-layout column>
          <v-layout>
            <v-checkbox
              v-model="contentsQueryUnpublishedOnly"
              class="mr-2 mt-1"
              hide-details
              color="msr"
              :label="$t('unpublishedOnly')"
            />
            <v-icon
              :color="contentsQueryUnpublishedOnly ? 'msr' : 'grey'"
              class="mt-1"
            >
              visibility_off
            </v-icon>
          </v-layout>
          <v-layout>
            <v-checkbox
              v-model="contentsQueryPublishedOnly"
              class="mr-2 mt-1"
              hide-details
              color="msr"
              :label="$t('publishedOnly')"
            />
            <v-icon
              :color="contentsQueryPublishedOnly ? 'msr' : 'grey'"
              class="mt-1"
            >
              visibility
            </v-icon>
          </v-layout>
        </v-layout>
      </v-list-tile>
      <!-- Date Filters -->
      <v-list-tile
        :disabled="disabled"
        class="list-tile-auto-height"
      >
        <v-layout
          align-center
          class="mb-1"
          nowrap
        >
          <v-checkbox
            v-model="filterByDate"
            class="msr--text mt-0 grow-1"
            color="msr"
            hide-details
            :disabled="showMinDatePickerMenu || showMaxDatePickerMenu"
            :label="$t('filterContentByDateLabel') + ':'"
            :title="$t('filterContentByDateTooltip')"
          />
          <!-- Min. Date -->
          <v-menu
            v-model="showMinDatePickerMenu"
            :close-on-content-click="false"
            :min-width="mobileDatepickerMinWidth"
            lazy
          >
            <v-text-field
              slot="activator"
              v-model="contentsQueryMinDate"
              readonly
              hide-details
              class="
                mt-1
                pt-0
                ml-2
                mr-0
                v-text-no-line-below
                date-picker-activator
                align-center
              "
              :placeholder="$t('from')"
              :title="$t('chooseDate')"
              @click.stop="showMinDatePickerMenu = !showMinDatePickerMenu"
            />
            <v-date-picker
              v-model="contentsQueryMinDate"
              color="msr"
              scrollable
              :no-title="$vuetify.breakpoint.xsOnly"
              :first-day-of-week="1"
              :locale="$t('_datepickerLocale')"
              @input="onMinDateChosen()"
            />
          </v-menu>
          <b class="grey--text mt-1 ml-2">-</b>
          <!-- Max. Date -->
          <v-menu
            v-model="showMaxDatePickerMenu"
            :close-on-content-click="false"
            :min-width="mobileDatepickerMinWidth"
            lazy
          >
            <v-text-field
              slot="activator"
              v-model="contentsQueryMaxDate"
              readonly
              hide-details
              class="
                mt-1
                pt-0
                ml-2
                mr-0
                v-text-no-line-below
                date-picker-activator
                align-center
              "
              :placeholder="$t('toIncluding')"
              :title="$t('chooseDate')"
              @click.stop="showMaxDatePickerMenu = !showMaxDatePickerMenu"
            />
            <v-date-picker
              v-model="contentsQueryMaxDate"
              color="msr"
              scrollable
              :no-title="$vuetify.breakpoint.xsOnly"
              :first-day-of-week="1"
              :locale="$t('_datepickerLocale')"
              :allowed-dates="isAllowedMaxDate"
              @input="onMaxDateChosen()"
            />
          </v-menu>
        </v-layout>
      </v-list-tile>
      <slot name="extraItems" />
    </v-list>
  </v-menu>
</template>

<script>
import dayjs from 'dayjs';

import mobileDatepickerMinWidth from '@/mixins/mobileDatepickerMinWidth.js';

import OnAirIndicator from '@/components/OnAirIndicator';

export default {
  components: {
    OnAirIndicator,
  },
  mixins: [mobileDatepickerMinWidth],
  props: {
    onAirOnly: {
      type: Boolean,
      default: false,
    },
    // Content types can be excluded from all computation.
    noRadio: {
      type: Boolean,
      default: false,
    },
    noTv: {
      type: Boolean,
      default: false,
    },
    noTickers: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /* Override internal dirty flag from outside. */
    forceDirty: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMenu: false,
      showMinDatePickerMenu: false,
      showMaxDatePickerMenu: false,
    };
  },
  computed: {
    filterByDate: {
      get() {
        return !!this.contentsQueryMinDate || !!this.contentsQueryMinDate;
      },
      set(flag) {
        if (flag) {
          if (!this.contentsQueryMinDate && !this.contentsQueryMaxDate) {
            this.showMinDatePickerMenu = true;
          }
        } else {
          this.contentsQueryMinDate = null;
          this.contentsQueryMaxDate = null;
        }
      },
    },
    /**
     * User needs to have uploadgroups, allowing him to publish/unpublish.
     */
    canSeePublishFilter() {
      return this.$user && this.$userprofile.can_upload_content;
    },
    contentsQuery() {
      return this.$store.state.common.contentsQuery;
    },
    contentsQueryRadio: {
      get() {
        return this.contentsQuery.radio;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_RADIO', flag);
      },
    },
    contentsQueryTV: {
      get() {
        return this.contentsQuery.tv;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_TV', flag);
      },
    },
    contentsQueryTickers: {
      get() {
        return this.contentsQuery.tickers;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_TICKERS', flag);
      },
    },
    contentsQueryOnAirOnly: {
      get() {
        return this.contentsQuery.onAirOnly;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_ON_AIR_ONLY', flag);
      },
    },
    contentsQueryEditableOnly: {
      get() {
        return this.contentsQuery.editableOnly;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_EDITABLE_ONLY', flag);
      },
    },
    contentsQueryUnpublishedOnly: {
      get() {
        return this.contentsQuery.unpublishedOnly;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_UNPUBLISHED_ONLY', flag);
      },
    },
    contentsQueryPublishedOnly: {
      get() {
        return this.contentsQuery.publishedOnly;
      },
      set(flag) {
        this.$store.commit('SET_CONTENTS_QUERY_PUBLISHED_ONLY', flag);
      },
    },
    contentsQueryMinDate: {
      get() {
        return this.contentsQuery.minDate;
      },
      set(date) {
        this.$store.commit('SET_CONTENTS_QUERY_MIN_DATE', date);
      },
    },
    contentsQueryMaxDate: {
      get() {
        return this.contentsQuery.maxDate;
      },
      set(date) {
        this.$store.commit('SET_CONTENTS_QUERY_MAX_DATE', date);
      },
    },
    /**
     * When all or no items are checked, we consider this as the
     * default, unchanged and therefore non-dirty state.
     *
     * Note: onAirOnly is not a content type, thus needs some inverted
     *   consideration here, not to be confused!
     *
     * Basically the default, 'not dirty', 'no filters applied' is one
     * of the two:
     *
     *   - Radio Spots [ ]
     *   - TV Clips    [ ]
     *   - TV Ticker   [ ]
     *   - ON AIR      [ ]
     *   - EDITABLE    [ ]
     *   - PUBLISH ... [ ]
     *   - DATE        [ ]
     *
     *   - Radio Spots [x]
     *   - TV Clips    [x]
     *   - TV Ticker   [x]
     *   - ON AIR      [ ]
     *   - EDITABLE    [ ]
     *   - PUBLISH ... [ ]
     *   - DATE        [ ]
     */
    dirty() {
      if (this.forceDirty) {
        return true;
      }

      const allSelected =
        this.contentsQueryRadio &&
        this.contentsQueryTV &&
        this.contentsQueryTickers &&
        !this.contentsQueryOnAirOnly &&
        !this.contentsEditableOnly &&
        !this.contentsQueryUnpublishedOnly &&
        !this.contentsQueryPublishedOnly &&
        !this.contentsQueryMinDate;
      const noneSelected =
        !this.contentsQueryRadio &&
        !this.contentsQueryTV &&
        !this.contentsQueryTickers &&
        !this.contentsQueryOnAirOnly &&
        !this.contentsEditableOnly &&
        !this.contentsQueryUnpublishedOnly &&
        !this.contentsQueryPublishedOnly &&
        !this.contentsQueryMinDate;
      const defaultState = allSelected || noneSelected;
      return !defaultState;
    },
  },
  watch: {
    filterByDate: function (filterByDate) {
      if (filterByDate) {
        // this.showMinDatePickerMenu = true;
      } else {
        this.contentsQueryMinDate = null;
        this.contentsQueryMaxDate = null;

        if (this.closeMenuOnContentClick) {
          this.showMenu = false;
        }
      }
    },
  },
  mounted() {
    this.$bus.$on('usercontents-date-filter-open-menu', () => {
      this.showMenu = true;
    });
  },
  methods: {
    isAllowedMaxDate(maxDateString) {
      if (!this.contentsQueryMinDate) {
        return true;
      }
      const minDate = dayjs(this.contentsQueryMinDate);
      const maxDate = dayjs(maxDateString);
      return maxDate.isSame(minDate) || maxDate.isAfter(minDate);
    },
    onMinDateChosen() {
      this.showMinDatePickerMenu = false;

      if (this.contentsQueryMaxDate) {
        const minDate = dayjs(this.contentsQueryMinDate);
        const maxDate = dayjs(this.contentsQueryMaxDate);
        if (maxDate.isBefore(minDate)) {
          this.contentsQueryMaxDate = null;
        }
      }
    },
    onMaxDateChosen() {
      this.showMaxDatePickerMenu = false;
    },
    reset() {
      this.$store.commit('RESET_CONTENT_FILTERS');
    },
  },
};
</script>

<style></style>

<style scoped>
.max-width-90 {
  max-width: 90%;
}

.typefilter-menu {
  min-width: 160px;
}

.v-input--radio-group
  >>> .v-radio:not(.v-radio--is-disabled)
  .v-input--selection-controls__input
  .v-icon.material-icons.accent--text {
  color: var(--msr-color-primary) !important;
}

.v-btn-toggle--selected {
  box-shadow: none;
}

.v-menu__content >>> .no-scrollbar {
  overflow-y: hidden;
}

.no-padding >>> .v-list__tile {
  padding: 0;
}

.date-picker-activator {
  max-width: 90px;
}
.v-input.date-picker-activator.align-right >>> input {
  text-align: right;
}
.v-input.date-picker-activator.align-center >>> input {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/ContentTypeFilterMenu.vue